import React, { useState, useCallback, useRef } from 'react';
import './App.css';
import AudioTrack from './components/AudioTrack';
import { getAllSounds, getRandomTracks, mixAndDownloadTracks } from './utils/soundUtils';

function App() {
  const [currentTracks, setCurrentTracks] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [globalPlaying, setGlobalPlaying] = useState(null);
  const [isGlobalPlaying, setIsGlobalPlaying] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const trackRefs = useRef([]);

  const shuffleTracks = useCallback(() => {
    setIsShuffling(true);
    setGlobalPlaying(null);
    setIsGlobalPlaying(false);
    
    // Small delay to show shuffle feedback
    setTimeout(() => {
      const allSounds = getAllSounds();
      const randomTracks = getRandomTracks(allSounds, 8);
      setCurrentTracks(randomTracks);
      trackRefs.current = [];
      setIsShuffling(false);
    }, 300);
  }, []);

  const toggleGlobalPlay = () => {
    if (isGlobalPlaying) {
      // Pause all tracks
      trackRefs.current.forEach(ref => {
        if (ref?.pause) ref.pause();
      });
      setGlobalPlaying(false);
      setIsGlobalPlaying(false);
    } else {
      // Play all loaded tracks
      const loadedRefs = trackRefs.current.filter(ref => ref?.isLoaded);
      if (loadedRefs.length > 0) {
        loadedRefs.forEach(ref => {
          if (ref?.play) ref.play();
        });
        setGlobalPlaying(true);
        setIsGlobalPlaying(true);
      }
    }
  };

  const handleTrackStateChange = (isPlaying) => {
    if (!isPlaying && isGlobalPlaying) {
      // If any track stops and we're in global play mode, stop global play
      setIsGlobalPlaying(false);
      setGlobalPlaying(null);
    }
  };

  const handleDownload = async () => {
    if (currentTracks.length === 0) return;
    
    setIsDownloading(true);
    try {
      await mixAndDownloadTracks(currentTracks);
    } finally {
      setIsDownloading(false);
    }
  };

  // Initialize with first shuffle
  React.useEffect(() => {
    shuffleTracks();
  }, [shuffleTracks]);

  return (
    <div className="min-h-screen w-screen bg-orange-100 py-0 px-0 md:py-8 md:px-4 bg-texture-overlay">
      <div className="min-h-screen md:min-h-0 w-full md:max-w-md mx-auto bg-amber-50 border-0 md:border-4 border-black p-4 md:scribble-shadow md:my-8">
        {/* Navbar */}
        <div className="mb-6 flex items-center justify-between border-b-4 border-black pb-3">
          <div className="w-8"></div> {/* Spacer for centering */}
          <h1 className="text-xl font-black text-black rough-text pixel-font">
            headache v1
          </h1>
          <button
            onClick={() => setShowInfoPopup(true)}
            className="w-8 h-8 border-2 border-black bg-gray-400 hover:bg-gray-300 flex items-center justify-center scribble-shadow scribble-shadow-hover"
          >
            <span className="font-bold text-black pixel-font">i</span>
          </button>
        </div>

        {/* Info Popup */}
        {showInfoPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-amber-50 border-4 border-black p-6 max-w-sm w-full scribble-shadow">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-black text-black rough-text pixel-font">
                  headache
                </h2>
                <button
                  onClick={() => setShowInfoPopup(false)}
                  className="w-6 h-6 border-2 border-black bg-gray-400 hover:bg-gray-300 flex items-center justify-center text-xs font-bold scribble-shadow scribble-shadow-hover"
                >
                  ×
                </button>
              </div>
              
              <p className="text-sm text-black mb-6 pixel-font leading-relaxed">
                Botanica/Petalcore style swell & impact generator, free and open-source :)
              </p>
              
              <p className="text-sm text-black mb-6 pixel-font leading-relaxed">
                All samples are in C major.
              </p>
              
              <div className="flex space-x-3">
                <a
                  href="https://twitter.com/@pointer_64"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 border-2 border-black bg-gray-400 hover:bg-gray-300 text-black font-bold pixel-font text-sm scribble-shadow scribble-shadow-hover"
                >
                  Twitter
                </a>
                <a
                  href="https://sofv.bandcamp.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 border-2 border-black bg-white hover:bg-gray-100 text-black font-bold pixel-font text-sm scribble-shadow scribble-shadow-hover"
                >
                  Bandcamp
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* Audio Tracks */}
        <div className="space-y-3 mb-8">
          {currentTracks.map((sound, index) => (
            <AudioTrack 
              key={`${sound.path}-${index}`} 
              ref={el => trackRefs.current[index] = el}
              sound={sound} 
              index={index}
              globalPlaying={globalPlaying}
              onGlobalPlayStateChange={handleTrackStateChange}
            />
          ))}
        </div>

        {/* Control Buttons */}
        <div className="flex justify-center space-x-2 flex-nowrap">
          {/* Global Play Button */}
          <button
            onClick={toggleGlobalPlay}
            disabled={currentTracks.length === 0}
            className={`w-12 h-12 border-4 border-black font-bold text-lg text-black transition-all transform scribble-shadow scribble-shadow-hover scribble-shadow-active flex items-center justify-center ${
              currentTracks.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : isGlobalPlaying
                ? 'bg-gray-400 hover:bg-gray-300'
                : 'bg-white hover:bg-gray-100'
            }`}
          >
            {isGlobalPlaying ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>

          {/* Shuffle Button */}
          <button
            onClick={shuffleTracks}
            disabled={isShuffling}
            className={`w-12 h-12 border-4 border-black transition-all transform scribble-shadow scribble-shadow-hover scribble-shadow-active flex items-center justify-center ${
              isShuffling
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gray-400 hover:bg-gray-300'
            }`}
          >
            {isShuffling ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10.59 9.17L5.41 4 4 5.41l5.17 5.17 1.42-1.41zM14.5 4l2.04 2.04L4 18.59 5.41 20 17.96 7.46 20 9.5V4h-5.5zm.33 9.41l-1.41 1.41 3.13 3.13L14.5 20H20v-5.5l-2.04 2.04-3.13-3.13z"/>
              </svg>
            )}
          </button>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            disabled={currentTracks.length === 0 || isDownloading}
            className={`w-12 h-12 border-4 border-black transition-all transform scribble-shadow scribble-shadow-hover scribble-shadow-active flex items-center justify-center ${
              currentTracks.length === 0 || isDownloading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-white hover:bg-gray-100'
            }`}
            title="Download mixed audio"
          >
            {isDownloading ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
